/**
 * "Show me in talent discovery" — the explicit opt-in to internal discovery.
 *
 * Separate from "public profile": publishing /u/:slug lets people who have the
 * link see it; discovery lists you where recruiters search, and lets them save,
 * evaluate and contact you. Off by default, for everyone.
 */

import { useGetMyDiscoveryQuery, useSetMyDiscoveryMutation } from '../../store/api/sharingApi';

export interface DiscoveryOptInToggleProps {
  optedIn: boolean;
  /** Separate choice: recruiters may contact you (only while you are discoverable). */
  contactAllowed?: boolean;
  onContactChange?: (next: boolean) => void;
  /** Whether the public profile is published (discovery also requires it). */
  isPublic: boolean;
  busy?: boolean;
  error?: string | null;
  onChange: (next: boolean) => void;
}

export function DiscoveryOptInToggle({ optedIn, contactAllowed = false, onContactChange, isPublic, busy = false, error = null, onChange }: DiscoveryOptInToggleProps) {
  return (
    <div className="card" data-testid="discovery-opt-in">
      <div className="card-header">
        <h3 className="font-semibold text-gray-900">Talent discovery</h3>
        <p className="text-sm text-gray-500 mt-1">
          Separate from your public profile. Off unless you turn it on.
        </p>
      </div>
      <div className="card-body space-y-3">
        <label className="flex items-start justify-between gap-4 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <div>
            <span className="font-medium text-gray-900">Show me in talent discovery</span>
            <p className="text-sm text-gray-600 mt-1">
              When on, recruiters and companies on BeHeard can find you in marketplace search and
              save or evaluate you. They see only the profile sections you have chosen to make
              public above. When off, you are not listed, and recruiters cannot save or evaluate
              you — even if your profile is public.
            </p>
          </div>
          <input
            type="checkbox"
            role="switch"
            aria-checked={optedIn}
            aria-label="Show me in talent discovery"
            checked={optedIn}
            disabled={busy}
            onChange={(e) => onChange(e.target.checked)}
            className="toggle mt-1"
          />
        </label>
        {onContactChange && (
          <label className="flex items-start justify-between gap-4 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer" data-testid="recruiter-contact-opt-in">
            <div>
              <span className="font-medium text-gray-900">Allow recruiters to contact me</span>
              <p className="text-sm text-gray-600 mt-1">
                A separate choice. When on, recruiters who find you in talent discovery can send you a
                contact request. It has no effect while you are not in talent discovery. Off unless you
                turn it on.
              </p>
            </div>
            <input
              type="checkbox"
              role="switch"
              aria-checked={contactAllowed}
              aria-label="Allow recruiters to contact me"
              checked={contactAllowed}
              disabled={busy}
              onChange={(e) => onContactChange(e.target.checked)}
              className="toggle mt-1"
            />
          </label>
        )}
        {optedIn && !isPublic && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="discovery-needs-public">
            You are opted in, but your profile is private, so you are not listed. Publish your
            profile for discovery to take effect.
          </p>
        )}
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

/** Connected card: reads and writes the caller's own preference. */
export function DiscoveryOptInCard({ isPublic }: { isPublic: boolean }) {
  const { data, isLoading } = useGetMyDiscoveryQuery();
  const [setDiscovery, { isLoading: isSaving, error }] = useSetMyDiscoveryMutation();

  return (
    <DiscoveryOptInToggle
      optedIn={data?.optedIn === true}
      contactAllowed={data?.recruiterContactAllowed === true}
      onContactChange={(next) => {
        void setDiscovery({ recruiterContactAllowed: next });
      }}
      isPublic={isPublic}
      busy={isLoading || isSaving}
      error={error ? 'Could not save your discovery preference. Nothing changed.' : null}
      onChange={(next) => {
        void setDiscovery({ optedIn: next });
      }}
    />
  );
}
